import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  OutputRef,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { merge, Observable, Subscription } from 'rxjs';

interface DropdownPanel {
  templateRef: TemplateRef<any>;
  readonly closed: EventEmitter<void> | OutputRef<void>;
}

@Directive({
  selector: '[appDropdownTriggerFor]',
  standalone: false,
})
export class DropdownTriggerForDirective implements OnDestroy {
  private isDropdownOpen = false;
  private overlayRef: OverlayRef;
  private dropdownClosingActionsSub = Subscription.EMPTY;

  @Input('appDropdownTriggerFor') public dropdownPanel: DropdownPanel;
  @Output() overlayClosed = new EventEmitter();

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2
  ) {}

  @HostListener('click') toggleDropdown(): void {
    this.isDropdownOpen ? this.destroyDropdown() : this.openDropdown();
  }

  openDropdown(): void {
    this.overlayClosed.emit(false);
    this.isDropdownOpen = true;
    this.renderer.addClass(this.elementRef.nativeElement, 'test-class');
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.close(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: -53,
          },
        ]),
    });

    const templatePortal = new TemplatePortal(
      this.dropdownPanel.templateRef,
      this.viewContainerRef
    );
    this.overlayRef.attach(templatePortal);

    this.dropdownClosingActionsSub = this.dropdownClosingActions().subscribe(() => {
      this.destroyDropdown();
    });
  }

  private dropdownClosingActions(): Observable<any> {
    const backdropClick$ = this.overlayRef.backdropClick();
    const detachment$ = this.overlayRef.detachments();
    const dropdownClosed =
      this.dropdownPanel.closed instanceof Observable
        ? this.dropdownPanel.closed
        : outputToObservable(this.dropdownPanel.closed);

    return merge(backdropClick$, detachment$, dropdownClosed);
  }

  private destroyDropdown(): void {
    if (!this.overlayRef || !this.isDropdownOpen) {
      return;
    }
    this.overlayClosed.emit(true);
    this.dropdownClosingActionsSub.unsubscribe();
    this.isDropdownOpen = false;
    this.overlayRef.detach();
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }
}
